
import { Component, Prop, Vue } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { SMUser, SMCases } from "@/store";
import { EMPLOYER_ROLE } from "@/constants/roles";
import { TO_DO_TASK_STATUS } from "@/constants/tasks";

import WidgetTemplate from "@/components/Widgets/Templates/WidgetTemplate.vue";
import Image from "@/assets/images/people/person-1.png";
import { API_TTask } from "@/models/tasks_services";
import { API_TCase } from "@/models/cases";

const CASE_STATUS_REPORT_GENERATION = "report_generation";

const REPORT_ALIAS = "ca_report_candidate_assessment";

const SUCCESS_HTTP_STATUS_CODE = 200;

const CASE_TIMEOUT = 30000;

const BUTTON_PROPS = {
  long: true,
  type: "primary",
};

@Component({
  components: {
    WidgetTemplate,
  },
})
export default class ReportGenerated extends Vue {
  @Prop({ type: String }) readonly jobTitle?: string;
  @Prop({ type: String }) readonly name?: string;
  @Prop({ type: Boolean }) readonly caseHasTask?: boolean;

  image = Image;

  BUTTON_PROPS = BUTTON_PROPS;

  get status() {
    return SMCases.caseStatus || CASE_STATUS_REPORT_GENERATION;
  }

  get list() {
    if (SMUser.user?.role === EMPLOYER_ROLE) return this.$t("list.employer");

    return this.$t("list.candidate");
  }

  get caseId(): string {
    return this.$route.params.id;
  }

  loading = false;

  async generateReport() {
    try {
      this.loading = true;

      SMCases.setCaseStatus(CASE_STATUS_REPORT_GENERATION);

      const { caseId } = this;

      if (!caseId) return;

      const url = API_ROUTES.CASE_REPORT_GENERATE(caseId, REPORT_ALIAS);

      await this.axios.get(url);
    } catch ({ response }) {
      if ((response as Response).status !== SUCCESS_HTTP_STATUS_CODE) {
        console.warn(response);
      }
    } finally {
      this.loading = false;

      await this.getCase();
    }
  }

  async getCase() {
    const { data } = await this.axios.get(API_ROUTES.CASE_ID(this.caseId));

    if (!data) return;

    SMCases.setCaseStatus(data.status);

    SMCases.setCase(data);

    await this.$nextTick();

    if (SMCases.caseStatus !== CASE_STATUS_REPORT_GENERATION) return;

    this.waitForReportStatus();
  }

  waitForReportStatus() {
    setTimeout(() => {
      this.getCase();
    }, CASE_TIMEOUT);
  }

  get tasks() {
    return (SMCases.case as API_TCase).tasks;
  }

  get toDoTasks() {
    return this.tasks?.filter((task) => task.status === TO_DO_TASK_STATUS);
  }

  get toDoTaskForCurrentUser() {
    // It is relevant if we have mounted user, not "expert";
    return this.toDoTasks?.filter((task) => task.user_id === SMUser.user?.ID);
  }

  get toDoTaskNotForCurrentUser() {
    // It is relevant if we have mounted user, not "expert";
    return this.toDoTasks?.filter((task) => task.user_id !== SMUser.user?.ID);
  }

  get disabledAnotherUserHasTasks() {
    return this.toDoTaskForCurrentUser?.length < 1 && this.toDoTaskNotForCurrentUser?.length > 0;
  }

  get tooltipMessage() {
    return this.toDoTasks?.length ? this.$t("tooltip") : null;
  }

  async startTheNextTask() {
    if (!this.toDoTaskForCurrentUser?.length) return;

    const task = this.toDoTaskForCurrentUser[0];

    const { ID: id } = (task as unknown) as API_TTask;

    const path = `/cases/tasks/${id}`;

    await this.$router.push({
      path,
      params: {
        status: TO_DO_TASK_STATUS,
      },
    });
  }

  get generateReportEnable() {
    if (this.loading) return false;

    if (SMUser.isExpert) return !this.toDoTasks.length;

    if (SMUser.isEmployer) {
      return !this.toDoTaskForCurrentUser?.length && !this.toDoTaskNotForCurrentUser?.length;
    }

    return !this.toDoTaskForCurrentUser?.length;
  }
}
